import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { fetchCleaners, updateCleaner, rotateCleanerToken } from "~/apis/cleaners"
import { fetchProperties } from "~/apis/properties"
import { messageLink, messageLinkProps, PLATFORM_LABELS } from "~/lib/messaging"
import { PageSpinner } from "~/components/Spinner"
import routes from "~/routes"

const CHANNEL_LABELS = { sms: "Messages (SMS/iMessage)", whatsapp: "WhatsApp" }

function CleanerRow({ cleaner, properties, onChange }) {
  const [ editing, setEditing ] = useState(false)
  const [ form, setForm ] = useState({})
  const [ error, setError ] = useState(null)
  const [ copied, setCopied ] = useState(false)

  function startEditing() {
    setForm({ name: cleaner.name, phone: cleaner.phone, email: cleaner.email ?? "", messaging_channel: cleaner.messaging_channel, property_id: String(cleaner.property_id) })
    setError(null)
    setEditing(true)
  }

  async function save(event) {
    event.preventDefault()
    setError(null)
    try {
      onChange(await updateCleaner(cleaner.id, form))
      setEditing(false)
    } catch (err) {
      setError(err.message)
    }
  }

  async function toggleActive() {
    setError(null)
    try {
      onChange(await updateCleaner(cleaner.id, { active: !cleaner.active }))
    } catch (err) {
      setError(err.message)
    }
  }

  async function rotate() {
    if (!window.confirm(`Make a new link for ${cleaner.name}? Their old link will stop working.`)) return
    onChange(await rotateCleanerToken(cleaner.id))
  }

  async function copyLink() {
    await navigator.clipboard.writeText(cleaner.portal_url)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const shareText = `Here's your Jacky Winter cleans page — save this link: ${cleaner.portal_url}`
  const upcoming = cleaner.upcoming_cleans_count ?? 0

  return (
    <li className={`rounded-xl border border-border bg-card p-4 ${cleaner.active ? "" : "opacity-60"}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-semibold text-foreground">
            {cleaner.name}
            {!cleaner.active && <Badge variant="outline" className="ml-2">inactive</Badge>}
            {cleaner.unresolved_notes_count > 0 && (
              <Badge variant="secondary" className="ml-2">{cleaner.unresolved_notes_count} note{cleaner.unresolved_notes_count === 1 ? "" : "s"}</Badge>
            )}
          </p>
          <p className="text-sm text-muted-foreground">{cleaner.phone}{cleaner.email && ` · ${cleaner.email}`}</p>
          <p className="text-sm text-muted-foreground">
            {cleaner.property_short_name} · {PLATFORM_LABELS[cleaner.messaging_channel] || cleaner.messaging_channel} · {upcoming} upcoming clean{upcoming === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          {!editing && <Button variant="outline" size="sm" onClick={startEditing}>Edit</Button>}
          <Button variant="outline" size="sm" onClick={toggleActive}>{cleaner.active ? "Deactivate" : "Reactivate"}</Button>
        </div>
      </div>

      {editing ? (
        <form onSubmit={save} className="mt-3 grid grid-cols-2 gap-3 border-t border-border pt-3">
          <div className="col-span-2">
            <Label htmlFor={`name-${cleaner.id}`}>Name</Label>
            <Input id={`name-${cleaner.id}`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label htmlFor={`phone-${cleaner.id}`}>Phone</Label>
            <Input id={`phone-${cleaner.id}`} type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label htmlFor={`email-${cleaner.id}`}>Email (optional)</Label>
            <Input id={`email-${cleaner.id}`} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label>Cleans at</Label>
            <Select value={form.property_id} onValueChange={(value) => setForm({ ...form, property_id: value })}>
              <SelectTrigger className="mt-1 w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                {properties.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.short_name ?? p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Messages via</Label>
            <Select value={form.messaging_channel} onValueChange={(value) => setForm({ ...form, messaging_channel: value })}>
              <SelectTrigger className="mt-1 w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CHANNEL_LABELS).map(([ value, label ]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 flex gap-2">
            <Button type="submit" size="sm">Save</Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </form>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2 border-t border-border pt-3">
          <Button variant="outline" size="sm" onClick={copyLink}>{copied ? "Copied ✓" : "Copy their link"}</Button>
          <Button asChild variant="outline" size="sm">
            <a href={messageLink(cleaner.phone, shareText, cleaner.messaging_channel)} {...messageLinkProps(cleaner.messaging_channel)}>💬 Send the link on {PLATFORM_LABELS[cleaner.messaging_channel] || "Messages"}</a>
          </Button>
          <Button variant="ghost" size="sm" onClick={rotate}>New link</Button>
        </div>
      )}
      {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
    </li>
  )
}

export default function Cleaners() {
  const [ cleaners, setCleaners ] = useState(null)
  const [ properties, setProperties ] = useState([])
  const [ error, setError ] = useState(null)

  useEffect(() => {
    fetchCleaners().then(setCleaners).catch((err) => setError(err.message))
    fetchProperties().then(setProperties).catch(() => {})
  }, [])

  function handleChange(updated) {
    setCleaners((current) => current.map((cleaner) => (cleaner.id === updated.id ? updated : cleaner)))
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-6 md:px-6 md:py-10">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">Cleaners</h1>
        <Button asChild size="sm">
          <Link to={routes.newCleaner}>+ Add cleaner</Link>
        </Button>
      </div>

      {error && <p className="mt-6 text-destructive">Couldn't load cleaners: {error}</p>}
      {!error && cleaners === null && <PageSpinner />}
      {cleaners?.length === 0 && (
        <p className="mt-6 rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
          Add your cleaners here — each gets a private link to see their cleans, say yes or no, and leave you notes.
        </p>
      )}
      {cleaners?.length > 0 && (
        <ul className="mt-6 space-y-3">
          {cleaners.map((cleaner) => (
            <CleanerRow key={cleaner.id} cleaner={cleaner} properties={properties} onChange={handleChange} />
          ))}
        </ul>
      )}
    </div>
  )
}
